import type { Injector, Signal } from '@angular/core'
import { Injectable, inject } from '@angular/core'
import type { Observable } from 'rxjs'
import { map } from 'rxjs'
import type { TaskList } from '@awork/features/task/models/task-list.model'
import { TaskListStore } from '@awork/features/task/state/task-list.store'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import { Order } from '@awork/core/state/signal-store/types'

@Injectable({ providedIn: 'root' })
export class TaskListQuery extends EntitySignalQuery<TaskList> {
  protected store: TaskListStore

  constructor() {
    const store = inject(TaskListStore)

    super(store)

    this.store = store
  }

  /**
   * Selects the task lists of a project
   * @param {Injector} injector Angular injector
   * @param {string} projectId
   * @param {boolean} [showArchived=false]
   * @returns {Observable<TaskList[]>}
   * @deprecated
   */
  selectByProject(injector: Injector, projectId: string, showArchived = false): Observable<TaskList[]> {
    return this.selectAll(injector, {
      filterBy: taskList => taskList.projectId === projectId && (!taskList.isArchived || showArchived),
      sortBy: 'order',
      sortByOrder: Order.ASC
    }).pipe(map(taskLists => this.mapEntities(taskLists)))
  }

  /**
   * Gets all task lists of a project
   * @param {string} projectId
   * @param {boolean} [showArchived=false]
   * @returns {TaskList[]}
   */
  getByProject(projectId: string, showArchived = false): TaskList[] {
    const taskLists = this.getAll({
      filterBy: taskList => taskList.projectId === projectId && (!taskList.isArchived || showArchived),
      sortBy: 'order',
      sortByOrder: Order.ASC
    })

    return this.mapEntities(taskLists)
  }

  /**
   * Gets all task lists of a project
   * @param {string} projectId
   * @param {boolean} [showArchived=false]
   * @returns {Signal<TaskList[]>}
   */
  queryByProject(projectId: string, showArchived = false): Signal<TaskList[]> {
    return this.queryAll({
      filterBy: taskList => taskList.projectId === projectId && (!taskList.isArchived || showArchived),
      sortBy: 'order',
      sortByOrder: Order.ASC
    })
  }

  /**
   * Queries a task list by ID
   * @param {string} taskListId
   * @returns {Signal<TaskList>}
   */
  queryTaskList(taskListId: string): Signal<TaskList> {
    return this.queryEntity(taskListId)
  }
}
